import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { useTranslation } from 'react-i18next';

const testimonialsFallback = [
  {
    quote: 'Great quality and long-lasting performance.',
    name: 'Herjuna Star',
    avatar: '/assets/placeholder.svg',
  },
  {
    quote: 'Reliable partner for our dealership network.',
    name: 'Alex Porter',
    avatar: '/assets/placeholder.svg',
  },
  {
    quote: 'Comfortable workwear that stands up to daily use.',
    name: 'Alex Porter',
    avatar: '/assets/placeholder.svg',
  },
];

const Testimonials = () => {
  const { t } = useTranslation();
  const testimonials =
    t('home.testimonials.items', { returnObjects: true }) ||
    testimonialsFallback;
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const timerRef = useRef(null);

  const start = useCallback(() => {
    clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setIndex((i) => (i + 1) % testimonials.length);
    }, 5000);
  }, [testimonials.length]);
  const stop = () => clearInterval(timerRef.current);

  useEffect(() => {
    start();
    return () => stop();
  }, [testimonials, start]);

  const slides = useMemo(
    () => [...testimonials, testimonials[0]],
    [testimonials]
  );
  return (
    <section className="px-4 sm:px-6 xl:px-8 py-20 md:py-[120px]">
      <div className="max-w-[1244px] w-full mx-auto">
        {/* Top row: heading + blurb */}
        <div className="flex lg:items-end items-center md:text-left text-center justify-between lg:flex-row flex-col gap-4">
          <div className="lg:max-w-[687px] w-full">
            <h2 className="libre-baskerville text-[32px] sm:text-[56px] leading-[40px] sm:leading-[69px] text-[#15202E] tracking-[-1.68px]">
              {t('home.testimonials.title')}
            </h2>
          </div>
          <div className="lg:max-w-[551px] w-full mb-4">
            <p className="text-[#525252] text-[14px] sm:text-[16px] leading-[25px] font-normal">
              {t('home.testimonials.subtitle')}
            </p>
          </div>
        </div>

        {/* Slider */}
        <div
          className="mt-2.5 flex justify-center"
          onMouseEnter={stop}
          onMouseLeave={start}
        >
          <div
            className="relative overflow-hidden rounded-[16px] max-w-[1244px] w-full bg-white"
            style={{ boxShadow: '4px 4px 48px 0px #1E25370F' }}
          >
            <div
              className={`flex ${animate ? 'transition-transform duration-500 ease-out' : ''}`}
              style={{
                width: `${slides.length * 100}%`,
                transform: `translateX(-${index * (100 / slides.length)}%)`,
              }}
              onTransitionEnd={() => {
                if (index === slides.length - 1) {
                  setAnimate(false);
                  setIndex(0);
                  requestAnimationFrame(() => setAnimate(true));
                }
              }}
            >
              {slides.map((item, i) => (
                <div
                  key={i}
                  style={{ width: `${100 / slides.length}%` }}
                  className="px-5 sm:px-8 lg:px-10 py-10 shrink-0"
                >
                  <div className="flex flex-col items-center text-center">
                    <img
                      src={item.avatar}
                      alt={item.name}
                      className="w-[72px] h-[72px] rounded-full ring-2 ring-white bg-white"
                    />
                    <p className="text-[#525252] text-[16px] sm:text-[21px] leading-[32px] mt-6 w-full">
                      “{item.quote}”
                    </p>
                    <div className="mt-10 flex items-center gap-6 sm:gap-10">
                      <h3 className="text-[#15202E] text-[28px] leading-[40px] font-normal libre-baskerville tracking-[-0.84px]">
                        {item.name}
                      </h3>
                      <div className="text-[#F59E0B] flex items-center">
                        <img src="/assets/star.svg" alt="star" />
                        <img src="/assets/star.svg" alt="star" />
                        <img src="/assets/star.svg" alt="star" />
                        <img src="/assets/star.svg" alt="star" />
                        <img src="/assets/star.svg" alt="star" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Dots */}
        <div className="mt-10 flex items-center justify-center gap-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => {
                setIndex(i);
                start();
              }}
              className={`w-2 h-2 rounded-full ${i === index % testimonials.length ? 'bg-[#00001D]' : 'bg-[#8F8F8F]'}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
